import { createFileRoute } from "@tanstack/react-router";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { generateBreadcrumbSchema, generateWebPageSchema } from "@/lib/utils";

const SITE_URL = "https://letslistbusiness.com";

const SECTIONS = [
  { id: "what-are-cookies", title: "What are cookies?", body: "Cookies are small text files stored on your device when you visit a website. They help us understand how you use LetsListBusiness, remember your preferences, and improve your overall experience." },
  { id: "how-we-use", title: "How we use cookies", body: "We use essential cookies to keep you logged in and secure your account. We also use analytics cookies to understand site traffic and usage patterns so we can optimize our directories and local guides." },
  { id: "types-of-cookies", title: "Types of cookies we use", body: "1. Essential Cookies: Required for site operation. 2. Performance Cookies: Used to gather anonymous analytics. 3. Functional Cookies: Remember your preferences, like your selected city." },
  { id: "your-choices", title: "Your choices", body: "You can control and manage cookies through your browser settings. Please note that disabling essential cookies may impact your ability to use certain features of LetsListBusiness, such as logging in or saving favorite listings." },
  { id: "updates", title: "Updates to this policy", body: "We may update this policy periodically to reflect changes in technology or legislation. Please check back regularly for updates." },
];

export const Route = createFileRoute("/cookies-policy/")({
  head: () => {
    const title = "Cookies Policy | LetsListBusiness";
    const description = "Learn about how LetsListBusiness uses cookies to improve your browsing experience and provide personalized local recommendations.";
    const url = SITE_URL + "/cookies-policy/";
    
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: "Cookies Policy" },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary" },
        { name: "twitter:title", content: "Cookies Policy | LetsListBusiness" },
        { name: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: url }],
      schema: [
        generateWebPageSchema(title, description, url),
        generateBreadcrumbSchema([
          { label: "Home", href: "/" },
          { label: "Cookies Policy", href: "/cookies-policy/" },
        ], SITE_URL),
      ],
    };
  },
  component: CookiesPolicyPage,
});

function CookiesPolicyPage() {
  return (
    <>
      <Breadcrumbs items={[{ label: "Home", href: "/" }, { label: "Cookies Policy" }]} />

      <section className="container mx-auto px-4 pb-16">
        <div className="grid gap-12 lg:grid-cols-12">
          {/* TOC sidebar */}
          <aside className="lg:col-span-3">
            <div className="sticky top-24">
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">On this page</div>
              <nav className="mt-4 space-y-1 border-l border-border">
                {SECTIONS.map((s, i) => (
                  <a key={s.id} href={`#${s.id}`} className="block border-l-2 border-transparent py-1.5 pl-4 text-sm text-muted-foreground transition-colors hover:border-primary hover:text-primary">
                    <span className="mr-2 font-mono text-[10px]">{String(i + 1).padStart(2, "0")}</span>
                    {s.title}
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          {/* Body */}
          <article className="lg:col-span-9">
            <div className="text-xs font-semibold uppercase tracking-[0.3em] text-primary">Legal</div>
            <h1 className="mt-3 font-display text-5xl font-bold">Cookies Policy</h1>
            <p className="mt-3 text-sm text-muted-foreground">Last updated: May 2026</p>

            <p className="mt-8 text-lg leading-relaxed text-foreground/85">
              This Cookies Policy explains how LetsListBusiness uses cookies and similar
              technologies to recognize you when you visit our website. It explains
              what these technologies are and why we use them.
            </p>

            <div className="mt-12 space-y-12">
              {SECTIONS.map((s, i) => (
                <section key={s.id} id={s.id} className="scroll-mt-24">
                  <div className="flex items-baseline gap-4">
                    <span className="font-mono text-sm text-primary">{String(i + 1).padStart(2, "0")}</span>
                    <h2 className="font-display text-2xl font-bold">{s.title}</h2>
                  </div>
                  <p className="mt-4 text-base leading-relaxed text-muted-foreground whitespace-pre-line">{s.body.replace(/\d\.\s/g, '\n$&').trim()}</p>
                </section>
              ))}
            </div>
          </article>
        </div>
      </section>
    </>
  );
}
